/** True when two adjacent frames may be linked for continuous playback (layout-independent). */
export function framesCanPlayContinuousTogether(
  _left: { layout: { width: number, height: number } },
  _right: { layout: { width: number, height: number } }
): boolean {
  return true
}

/**
 * Clears operator-set `playContinuousWithNext` on every frame (feature removed from the editor).
 * Runtime may still inject the flag during playback resolve (e.g. portal book).
 */
export function stripPlayContinuousFromFrames<T extends { playContinuousWithNext?: boolean }>(
  frames: readonly T[]
): T[] {
  return frames.map((f) => {
    if (f.playContinuousWithNext !== true) {
      return f
    }
    const { playContinuousWithNext: _omit, ...rest } = f
    return rest as T
  })
}

/**
 * Clears `playContinuousWithNext` on the last frame (no successor). Call after reorder/remove
 * and rely on {@link playlistSchema} transform for API-bound playlists.
 */
export function normalizeFrameContinuityFlags<T extends { playContinuousWithNext?: boolean, layout: { width: number, height: number } }>(
  frames: T[]
): T[] {
  const n = frames.length
  if (n === 0) {
    return frames
  }
  return frames.map((f, i) => {
    const stripFlag = (): T => {
      const { playContinuousWithNext: _omit, ...rest } = f
      return rest as T
    }
    if (f.playContinuousWithNext !== true) {
      return f
    }
    if (i === n - 1) {
      return stripFlag()
    }
    return f
  })
}

/**
 * Inserts `newFrame` at `insertAt` and, if the previous frame was linked into its former
 * successor, links the new frame into that successor so the chain is preserved (A→B becomes A→new→B).
 */
/** True if inserting at `insertAt` would sit between a frame and its linked successor (not at 0 or at append). */
export function insertWouldSpliceIntoContinuousLink<T extends { playContinuousWithNext?: boolean }>(
  frames: readonly T[],
  insertAt: number
): boolean {
  const at = Math.floor(insertAt)
  const n = frames.length
  if (at < 1 || at >= n) {
    return false
  }
  return frames[at - 1]?.playContinuousWithNext === true
}

export function insertFramePreservingContinuity<T extends { playContinuousWithNext?: boolean, layout: { width: number, height: number } }>(
  frames: T[],
  insertAt: number,
  newFrame: T
): T[] {
  const n = frames.length
  const at = Math.max(0, Math.min(Math.floor(insertAt), n))
  const prevLinkedIntoFormerNext = at > 0 && frames[at - 1]!.playContinuousWithNext === true
  const copy = [...frames]
  copy.splice(at, 0, newFrame)
  if (prevLinkedIntoFormerNext && at < copy.length - 1) {
    copy[at] = { ...copy[at]!, playContinuousWithNext: true }
  }
  return normalizeFrameContinuityFlags(copy)
}
